import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { configureStore } from '@reduxjs/toolkit';
import authReducer from '@food/store/authSlice';
import nutritionReducer from '@food/store/nutritionSlice';
import FoodApp from '@food/App';
import api from '../services/api';
import { isFoodSubscribeRequired, normalizeFoodConnectionError, redirectToContratarFood } from '../utils/foodModule';
import { getShellToken, setShellToken, sanitizeSharedStorageForShell } from '../utils/shellToken';
import { setFoodStorageItem, getFoodStorageItem } from '@food/utils/embeddedStorage';
import { startIdleSessionWatcher, touchSessionActivity } from '../utils/idleSession';
import { applyFoodDocumentBrand, resolveFoodBasename } from '../utils/foodPublicBrand';
import './food-tailwind.css';

const foodStore = configureStore({
  reducer: {
    auth: authReducer,
    nutrition: nutritionReducer,
  },
});

function applyFoodSession(dispatch, payload) {
  setFoodStorageItem('token', payload.accessToken);
  setFoodStorageItem('refresh', payload.refreshToken || null);
  setFoodStorageItem('user', JSON.stringify(payload.user));
  setFoodStorageItem('subscription', JSON.stringify(payload.subscription || null));
  // Opción A: chrome Food siempre FOOD PLAN — no inyectar branding gym/coach.
  try { localStorage.removeItem('shellBranding'); } catch { /* noop */ }
  dispatch({
    type: 'auth/login/fulfilled',
    payload: {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      user: payload.user,
      subscription: payload.subscription,
    },
  });
}

function bootstrapAuthFromStorage(dispatch) {
  const accessToken = getFoodStorageItem('token');
  const user = getFoodStorageItem('user');
  const subscription = getFoodStorageItem('subscription');
  if (accessToken && user) {
    dispatch({
      type: 'auth/login/fulfilled',
      payload: {
        accessToken,
        refreshToken: getFoodStorageItem('refresh'),
        user: JSON.parse(user),
        subscription: subscription ? JSON.parse(subscription) : null,
      },
    });
  }
}

function FoodShellSsoGate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [needsSubscribe, setNeedsSubscribe] = useState(false);
  const startedRef = useRef(false);
  const coachParam = params.get('coach') === '1' || params.get('dest') === '/trainer';
  if (!coachParam) {
    try { sessionStorage.removeItem('d28d_food_coach_tracking'); } catch { /* noop */ }
  }
  const coachTracking = coachParam
    || sessionStorage.getItem('d28d_food_coach_tracking') === '1';

  useEffect(() => {
    startedRef.current = false;
  }, [attempt]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    setLoading(true);
    setError('');
    setNeedsSubscribe(false);

    const relaunch = params.get('relaunch') === '1';
    const handoff = relaunch
      ? null
      : (params.get('token') || sessionStorage.getItem('d28d_food_handoff'));
    sessionStorage.removeItem('d28d_food_handoff');

    const shellToken = getShellToken();
    if (!handoff && !shellToken) {
      setError('Token SSO no recibido. Vuelve al inicio y abre FOOD PLAN de nuevo.');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        if (shellToken) {
          setShellToken(shellToken);
        }
        localStorage.setItem('d28d_shell', 'true');
        localStorage.setItem('d28d_shell_label', 'FOOD PLAN');

        let payload = null;

        // Con sesión del ecosistema (relaunch=1) solo exchange-session: no reenviar un jti gastado.
        if (handoff && !shellToken) {
          const { data } = await api.post(
            '/food-module/exchange',
            { token: handoff },
            { skipShellAuth: true, skipAuthClearOn401: true },
          );
          payload = data?.data || data;
        }

        if (!payload?.accessToken && shellToken) {
          const { data } = await api.post(
            '/food-module/exchange-session',
            {},
            {
              headers: { Authorization: `Bearer ${shellToken}` },
              skipShellAuth: true,
            },
          );
          payload = data?.data || data;
        }

        if (!payload?.accessToken) {
          setError('No se recibió sesión FOOD PLAN. Tu cuenta se creará al reintentar si tienes licencia activa.');
          setLoading(false);
          return;
        }

        applyFoodSession(dispatch, payload);
        sanitizeSharedStorageForShell();
        const role = String(payload?.user?.role || '').toUpperCase();
        const roleHome =
          role === 'SUPER_ADMIN' ? '/superadmin'
          : role === 'ADMIN' ? '/admin'
          : role === 'TRAINER' ? '/trainer'
          : '/dashboard';
        const dest = params.get('dest')
          || sessionStorage.getItem('d28d_food_dest')
          || roleHome;
        sessionStorage.removeItem('d28d_food_dest');
        if (coachTracking) sessionStorage.removeItem('d28d_food_coach_tracking');
        navigate(dest.startsWith('/') ? dest : `/${dest}`, { replace: true });
      } catch (err) {
        const normalized = normalizeFoodConnectionError(err);
        const status = err.response?.status;
        if (status === 503 || status === 502 || normalized.code === 'food_not_configured' || normalized.code === 'food_timeout') {
          setError(
            normalized.error
            || (coachTracking
              ? 'FOOD PLAN no está disponible. Reintenta; si persiste, el backend Food no responde (SSO).'
              : 'FOOD PLAN no está disponible ahora. Reintenta en unos segundos.'),
          );
        } else if (!coachTracking && (isFoodSubscribeRequired(normalized) || status === 403)) {
          setNeedsSubscribe(true);
          setError(normalized.error || 'Contrata FOOD PLAN desde Mi Cuenta para acceder al módulo.');
        } else {
          setError(coachTracking
            ? (normalized.error || 'No se pudo abrir el panel entrenador. Pulsa Reintentar.')
            : (normalized.error || 'Error conectando con FOOD PLAN'));
        }
        setLoading(false);
      }
    })();
  }, [params, navigate, dispatch, attempt]);

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-gray-50">
      <div className="text-center max-w-md space-y-4">
        <h1 className="text-xl font-semibold text-primary-700">
          {coachTracking ? 'Panel entrenador FOOD' : 'FOOD PLAN'}
        </h1>
        {error ? (
          <>
            <p className="text-red-600">{error}</p>
            <div className="flex flex-wrap justify-center gap-3">
              {needsSubscribe ? (
                <button
                  type="button"
                  className="btn-primary text-sm inline-flex items-center"
                  onClick={() => redirectToContratarFood()}
                >
                  Ir a Mi Cuenta
                </button>
              ) : (
                <button type="button" className="btn-primary text-sm" onClick={() => setAttempt((n) => n + 1)}>
                  Reintentar
                </button>
              )}
              {(() => {
                try {
                  if (sessionStorage.getItem('shell_can_return_ecosystem') === '1') {
                    return (
                      <a href="/" target="_top" rel="noopener noreferrer" className="btn-secondary text-sm inline-flex items-center">
                        Volver al ecosistema
                      </a>
                    );
                  }
                } catch { /* noop */ }
                return null;
              })()}
            </div>
          </>
        ) : (
          <p className="text-slate-600">{loading ? 'Conectando tu sesión…' : 'Redirigiendo…'}</p>
        )}
      </div>
    </div>
  );
}

function FoodPlanRoutes() {
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.endsWith('/shell-sso')) return;
    bootstrapAuthFromStorage(dispatch);
  }, [dispatch, location.pathname]);

  return (
    <div className="food-plan-root min-h-screen">
      <Routes>
        <Route path="/shell-sso" element={<FoodShellSsoGate />} />
        <Route path="/*" element={<FoodApp />} />
      </Routes>
    </div>
  );
}

export default function FoodPlanShell() {
  const foodBasename = resolveFoodBasename(
    typeof window !== 'undefined' ? window.location.pathname : '/food',
  );

  useEffect(() => {
    localStorage.setItem('d28d_shell', 'true');
    localStorage.setItem('d28d_shell_label', 'FOOD PLAN');
    try { localStorage.removeItem('shellBranding'); } catch { /* noop */ }
    document.documentElement.removeAttribute('data-coach-wl');
    document.documentElement.style.removeProperty('--brand-primary');
    document.documentElement.style.removeProperty('--brand-secondary');
    document.body.classList.add('food-plan-active');
    document.documentElement.classList.add('food-plan-active');
    document.body.style.backgroundColor = '#f9fafb';
    document.body.style.color = '#111827';
    const clearBrand = applyFoodDocumentBrand();
    return () => {
      document.body.classList.remove('food-plan-active');
      document.documentElement.classList.remove('food-plan-active');
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      if (typeof clearBrand === 'function') clearBrand();
    };
  }, []);

  // Food se monta fuera de AuthProvider: misma política de 1h de inactividad.
  useEffect(() => {
    const hasSession = () => Boolean(getShellToken() || getFoodStorageItem('token'));
    if (hasSession()) touchSessionActivity();
    return startIdleSessionWatcher({ isActive: hasSession });
  }, []);

  return (
    <Provider store={foodStore}>
      <BrowserRouter basename={foodBasename}>
        <FoodPlanRoutes />
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      </BrowserRouter>
    </Provider>
  );
}
